import { Download } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useError } from '@components/hooks/useError';
import { TableComponent } from '@components/table';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { getCdnImage } from '@components/utils/cdn-image';
import { links } from '@core/router';
import { PreviewFormFields } from './components/preview-form';
import { DEFAULT_FORM, ExportFormat, useStore } from './store';
import { MemberRow, PreviewForm, PreviewType, TeamRow } from './types';

const Avatar = ({ src }: { src?: string }) => (
    <div className="size-8 shrink-0 overflow-hidden rounded-full bg-muted">
        {src && <img src={src} className="size-full object-cover" alt="" />}
    </div>
);

export const PreviewLeaderboards = () => {
    const { communityId } = useParams();
    const numericCommunityId = Number(communityId);
    const {
        teams,
        memberPreview,
        teamPreview,
        run,
        changePage,
        changePageSize,
        exportLeaderboard
    } = useStore(numericCommunityId);
    const showError = useError();

    const [form, setForm] = React.useState<PreviewForm>(DEFAULT_FORM);
    const [activeType, setActiveType] = React.useState<PreviewType | null>(
        null
    );
    const [exporting, setExporting] = React.useState(false);

    const onRun = () => {
        setActiveType(form.type);
        run(form);
    };

    const onExport = async (exportFormat: ExportFormat) => {
        setExporting(true);
        try {
            await exportLeaderboard(exportFormat);
        } catch (e) {
            showError(e);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardContent>
                    <PreviewFormFields
                        form={form}
                        onChange={setForm}
                        teams={teams.result || []}
                        loading={memberPreview.loading || teamPreview.loading}
                        onRun={onRun}
                    />
                </CardContent>
            </Card>

            {activeType && (
                <div className="flex justify-end">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                loading={exporting}
                                disabled={
                                    memberPreview.loading || teamPreview.loading
                                }
                            >
                                <Download />
                                Export
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => onExport('csv')}>
                                CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onExport('json')}>
                                JSON
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}

            {activeType === 'COMMUNITY_TEAMS' && (
                <TableComponent<TeamRow[]>
                    items={teamPreview}
                    tableLayout="fixed"
                    rowsPerPageOptions={[100, 1000, 10000]}
                    columns={[
                        {
                            Header: 'Position',
                            accessor: 'position',
                            style: { width: 90 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Team',
                            id: 'team',
                            disableSortBy: true,
                            accessor: (entry) => (
                                <div className="flex items-center gap-3">
                                    <Avatar
                                        src={getCdnImage(
                                            entry.image.bucketName,
                                            entry.image.objectKey,
                                            {
                                                resize: {
                                                    width: 48,
                                                    height: 48
                                                }
                                            }
                                        )}
                                    />
                                    <span className="truncate">
                                        {entry.name}
                                    </span>
                                </div>
                            )
                        },
                        {
                            Header: 'Members',
                            accessor: (entry) => String(entry.memberCount),
                            id: 'memberCount',
                            style: { width: 120 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Points',
                            accessor: (entry) => String(entry.points),
                            id: 'points',
                            style: { width: 140 },
                            disableSortBy: true
                        }
                    ]}
                    onChangePage={changePage}
                    onPageSizeChange={changePageSize}
                />
            )}

            {(activeType === 'COMMUNITY_MEMBERS' ||
                activeType === 'TEAM_MEMBERS') && (
                <TableComponent<MemberRow[]>
                    items={memberPreview}
                    tableLayout="fixed"
                    rowsPerPageOptions={[100, 1000, 10000]}
                    columns={[
                        {
                            Header: 'Position',
                            accessor: 'position',
                            style: { width: 90 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Member',
                            id: 'member',
                            disableSortBy: true,
                            accessor: (entry) => (
                                <Link
                                    to={links.userDetails(entry.userId)}
                                    className="flex items-center gap-3 text-inherit no-underline"
                                >
                                    <Avatar
                                        src={getCdnImage(
                                            entry.image.bucketName,
                                            entry.image.objectKey,
                                            {
                                                resize: {
                                                    width: 48,
                                                    height: 48
                                                }
                                            }
                                        )}
                                    />
                                    <div className="min-w-0">
                                        <p className="truncate hover:underline">
                                            {entry.name}
                                        </p>
                                        <p className="truncate text-sm text-muted-foreground">
                                            {entry.userId}
                                        </p>
                                    </div>
                                </Link>
                            )
                        },
                        {
                            Header: 'Points',
                            accessor: (entry) => String(entry.points),
                            id: 'points',
                            style: { width: 140 },
                            disableSortBy: true
                        }
                    ]}
                    onChangePage={changePage}
                    onPageSizeChange={changePageSize}
                />
            )}
        </div>
    );
};

export default PreviewLeaderboards;
